import {
  RealityDimensionId,
  AbstractDimensionId,
  DimensionKey,
  QuizResult,
  ResultMetric,
} from '../types';
import { FORMAL_QUESTIONS } from '../data/mockQuizData';
import {
  CHARACTER_REGISTRY,
  CharacterDefinition,
  Dimension12D,
} from '../data/characterRegistry';
import { CHARACTER_PROFILES } from '../data/characterProfiles';
import { getUnifiedLifestyleMetrics } from './lifestyleMetricsEngine';

export const DIMS_12: Dimension12D[] = [
  'C', 'E', 'I', 'S', 'M', 'P', 'V', 'N', // 现实 8 维
  'X', 'A', 'B', 'R',                   // 抽象 4 维
];

export const ALL_DIMS: DimensionKey[] = [...DIMS_12, 'G'];

// 1. 动态自愈计算 35 题全部维度的理论最小值与最大值
export interface DimensionBounds {
  min: number;
  max: number;
}

export function computeTheoreticalBounds(): Record<DimensionKey, DimensionBounds> {
  const bounds: Record<DimensionKey, DimensionBounds> = {} as any;
  for (const d of ALL_DIMS) {
    bounds[d] = { min: 0, max: 0 };
  }

  for (const q of FORMAL_QUESTIONS) {
    for (const d of ALL_DIMS) {
      let qMin = 0;
      let qMax = 0;
      for (const opt of q.options) {
        const score = opt.scores[d] || 0;
        if (score < qMin) qMin = score;
        if (score > qMax) qMax = score;
      }
      bounds[d].min += qMin;
      bounds[d].max += qMax;
    }
  }

  return bounds;
}

export const THEORETICAL_BOUNDS = computeTheoreticalBounds();

// 2. 权重矩阵定义（严格遵循 12D 文档标准）
export const REAL_POOL_WEIGHTS: Record<Dimension12D, number> = {
  C: 1.2,
  E: 1.2,
  I: 1.0,
  S: 1.0,
  M: 1.3,
  P: 1.2,
  V: 0.9,
  N: 1.0,
  X: 0.2,
  A: 0.2,
  B: 0.2,
  R: 0.2,
};

export const ABSTRACT_POOL_WEIGHTS: Record<Dimension12D, number> = {
  X: 1.5,
  A: 1.7,
  B: 1.3,
  R: 1.4,
  C: 0.5,
  E: 0.5,
  I: 0.5,
  S: 0.4,
  M: 0.2,
  P: 0.2,
  V: 0.6,
  N: 0.8,
};

// 3. 用户作答累积分计算
export function calculateRawScores(
  answers: Record<number, string>
): Record<DimensionKey, number> {
  const raw: Record<DimensionKey, number> = {} as any;
  for (const d of ALL_DIMS) {
    raw[d] = 0;
  }

  for (const q of FORMAL_QUESTIONS) {
    const selectedOptId = answers[q.id];
    if (!selectedOptId) continue;
    const opt = q.options.find((o) => o.id === selectedOptId);
    if (!opt) continue;

    for (const [d, val] of Object.entries(opt.scores)) {
      if (val !== undefined) {
        raw[d as DimensionKey] += val;
      }
    }
  }

  return raw;
}

// 4. Min-Max 归一化 (0 - 100)
export function normalizeScores(
  rawScores: Record<DimensionKey, number>
): Record<Dimension12D, number> {
  const normalized: Record<Dimension12D, number> = {} as any;

  for (const d of DIMS_12) {
    const raw = rawScores[d] ?? 0;
    const { min, max } = THEORETICAL_BOUNDS[d];

    if (max === min) {
      normalized[d] = 50;
    } else {
      const val = ((raw - min) / (max - min)) * 100;
      normalized[d] = Math.round(Math.max(0, Math.min(100, val)));
    }
  }

  return normalized;
}

// 5. 角色坐标映射 (1~5 -> 0~100)
export function getCharacterCoords100(
  char: CharacterDefinition
): Record<Dimension12D, number> {
  const coords100: Record<Dimension12D, number> = {} as any;
  for (const d of DIMS_12) {
    const v = char.coordinates[d] ?? 3;
    coords100[d] = (v - 1) * 25;
  }
  return coords100;
}

// 6. 奖池分流与抽象越权判定 (Pool Routing)
export interface RoutingResult {
  pool: 'real' | 'abstract';
  realityRisk: number;
  isAbstractOverride: boolean;
  reason: string;
}

export function routePool(
  rawScores: Record<DimensionKey, number>,
  normalized12D: Record<Dimension12D, number>
): RoutingResult {
  const G = rawScores.G ?? 0;
  const { A, X, R, B } = normalized12D;

  // 抽象越权判定规则：
  // 即使现实条件合格 (G <= 3)，若抽象、失控或逆反处于极端高位，系统强制判定进入抽象池
  const isAbstractOverride =
    (A >= 85 && X >= 75) ||
    (A >= 80 && R >= 80) ||
    (A >= 75 && X >= 70 && R >= 70);

  if (isAbstractOverride) {
    return {
      pool: 'abstract',
      realityRisk: G,
      isAbstractOverride: true,
      reason: '【系统越权】虽然现实养护风险处于安全区间，但受试者的抽象/失控/逆反维度已突破常规碳基生命耐受上限，强制进入高维抽象收容池。',
    };
  }

  if (G >= 7) {
    return {
      pool: 'abstract',
      realityRisk: G,
      isAbstractOverride: false,
      reason: '【现实防线熔断】现实空间、时间或经济养护风险过高 (G >= 7)，不宜饲养常规现实宠物，进入抽象生物池。',
    };
  }

  if (G <= 3) {
    return {
      pool: 'real',
      realityRisk: G,
      isAbstractOverride: false,
      reason: '【现实条件达标】受试者现实生活方式相对稳固自律 (G <= 3)，适合饲养常规真实宠物。',
    };
  }

  // 4 <= G <= 6: 共同竞争过渡区间
  // 观察用户的抽象四维均值是否超过现实自律水平
  const abstractMean = (A + X + B + R) / 4;
  if (abstractMean >= 50) {
    return {
      pool: 'abstract',
      realityRisk: G,
      isAbstractOverride: false,
      reason: '【临界判定】现实风险处于临界过渡带 (G=4~6)，受试者深层精神状态偏向荒诞解构，分流至抽象生物池。',
    };
  }

  return {
    pool: 'real',
    realityRisk: G,
    isAbstractOverride: false,
    reason: '【临界判定】现实风险处于临界过渡带 (G=4~6)，受试者生活责任感尚存，分流至现实宠物池。',
  };
}

// 7. 加权欧氏距离与匹配度计算 (Character Matcher)
export interface ScoredCandidate {
  character: CharacterDefinition;
  distance: number;
  matchRate: string; // e.g. "91.8%"
  rawRate: number;
}

export interface MatchResult {
  userScores12D: Record<Dimension12D, number>;
  rawScores: Record<DimensionKey, number>;
  routing: RoutingResult;
  topMatch: ScoredCandidate;
  top3: ScoredCandidate[];
  allCandidates: ScoredCandidate[];
}

export function matchCharacter(
  normalized12D: Record<Dimension12D, number>,
  rawScores: Record<DimensionKey, number>,
  forcedPool?: 'real' | 'abstract'
): MatchResult {
  const routing = routePool(rawScores, normalized12D);
  const pool = forcedPool ?? routing.pool;
  const weights = pool === 'real' ? REAL_POOL_WEIGHTS : ABSTRACT_POOL_WEIGHTS;

  const candidates = CHARACTER_REGISTRY.filter((c) => c.pool === pool);

  const scoredCandidates: ScoredCandidate[] = candidates.map((char) => {
    const charCoords = getCharacterCoords100(char);

    let weightedSqDist = 0;
    let totalWeight = 0;

    for (const d of DIMS_12) {
      const w = weights[d] ?? 1.0;
      const diff = normalized12D[d] - charCoords[d];
      weightedSqDist += w * diff * diff;
      totalWeight += w;
    }

    const weightedRms = Math.sqrt(weightedSqDist / totalWeight);

    // 匹配度换算公式：将加权均方根误差平滑缩放为拟真心理测试百分比 (通常 75% ~ 98%)
    const rawRate = Math.max(0, Math.min(100, 100 - weightedRms * 0.85));
    const matchRate = `${rawRate.toFixed(1)}%`;

    return {
      character: char,
      distance: weightedRms,
      matchRate,
      rawRate,
    };
  });

  scoredCandidates.sort((a, b) => a.distance - b.distance);

  return {
    userScores12D: normalized12D,
    rawScores,
    routing: { ...routing, pool },
    topMatch: scoredCandidates[0],
    top3: scoredCandidates.slice(0, 3),
    allCandidates: scoredCandidates,
  };
}

// 8. 适配生成 ResultCard 所需的完整数据模型
export function generateResultViewData(matchResult: MatchResult): QuizResult {
  const { topMatch, routing, userScores12D } = matchResult;
  const char = topMatch.character;
  const isDanger = char.pool === 'abstract';

  // 统一计算 8 个固定生活方式指标（严格取自用户 12D 前 8 维）
  const unifiedMetrics = getUnifiedLifestyleMetrics(userScores12D, char);

  // 优先加载专属个性化角色档案
  const profile = CHARACTER_PROFILES[char.id];
  if (profile) {
    return {
      id: char.id,
      caseId: `SPECIMEN #${profile.badgeType === 'danger' ? 'ABST' : 'PET'}-${char.id.toUpperCase().slice(0, 4)}-${Math.floor(1000 + Math.random() * 9000)}`,
      matchRate: topMatch.matchRate,
      targetName: char.name,
      subtitle: profile.subtitle,
      categoryBadge: profile.categoryBadge,
      badgeType: profile.badgeType,
      imageUrl: char.image,
      imageAlt: `低多边形风格${char.name}全身大图`,
      systemVerdict: profile.systemVerdict,
      detailedAnalysis: profile.detailedAnalysis,
      metrics: unifiedMetrics,
      stampText: profile.stampText,
      stampType: profile.stampType,
      coexistenceAdvice: profile.coexistenceAdvice,
      confidentialLevel: profile.confidentialLevel,
    };
  }

  // 兜底动态构建（若无专属档案）
  const metrics: ResultMetric[] = unifiedMetrics;

  // 动态构建中枢判定报告书长文
  const kwList = char.personaKeywords.join('、');
  let systemVerdict = '';
  let detailedAnalysis: string[] = [];

  if (isDanger) {
    systemVerdict = `系统综合鉴定：在现有已知的 428 种常规家养哺乳动物中，没有任何一种碳基生物能够安全承受你当前的精神状态与生活作息。若强行匹配常规猫狗，极易造成宠物单方面抑郁、无故脱毛或过早看破红尘。\n\n依照《非常规生物收容与精神互补条例》第七款，系统已紧急越权调用低多边形高维收容库，正式指派「${char.name}」接管你的精神生活。其核心特质【${kwList}】将与你的意识形态达成宿命级闭环。`;
    detailedAnalysis = [
      `1. 精神同频共振：受试者的 12D 心理画像在抽象值 (${userScores12D.A}%) 与逆反/失控维度表现出极强的解构本能。该角色自带强大的降智与抗内耗力场，能用最纯粹的非理性荒诞，强行物理阻断你深夜的焦虑回路。`,
      `2. 极低现实维护负担：该收容体无需每日清晨风雨无阻下楼遛弯，更无现实掉毛与打翻花瓶之虞。其主要生存燃料为高热量外卖碳水、无厘头冲浪短视频与荒谬生活素材。在遭遇现实不可调和的矛盾时，它将与你携手执行终极策略——以极其超前的精神状态面对一切。`,
      `3. 宿命级双向同化：匹配度高达 ${topMatch.matchRate}。在漫长的人生荒原里，你们注定将在同一个屋檐下，把平淡甚至是充满荒谬的生活过成一部令人捧腹的黑色幽默短剧。`,
    ];
  } else {
    systemVerdict = `系统综合鉴定：恭喜你，你的作息曲线与精神健全度奇迹般地抵御住了测试后半段的全部降智诱导。你拥有极强的社会化自律能力、规律的物理运动习惯，以及对真实生命沉甸甸的托底耐心。\n\n「${char.name}」是经过系统 35 维全量变量矩阵测算后，与你现实生活方式契合度高达 ${topMatch.matchRate} 的最佳伴侣生灵。其【${kwList}】的生命特质将成为你现实生活最稳固的情绪锚点。`;
    detailedAnalysis = [
      `1. 生活节奏完美咬合：受试者的陪伴欲望 (${userScores12D.C}%) 与照护耐受度 (${userScores12D.M}%) 均处于健康充沛区间。照顾它的饮食起居对你而言绝非精力消耗，而是将你从虚无或机械的工作中强行拉回真实烟火人间的治愈契机。`,
      `2. 纯粹而长久的情感反馈：它毫无保留的信赖与每一次开门时的热情互动，能够百分百抵消你在外部现实世界中所遭受的所有冷漠、疲惫与委屈。`,
      `3. 成熟契约自洽：你拥有将一段长达十余年生命契约善始善终的成熟心智，你们的共处将成为文明人类与忠诚生灵之间最典范的生活诗篇。`,
    ];
  }

  const coexistenceAdvice = isDanger
    ? `⚠️ 饲养警示：切勿试图用传统世俗价值观对其进行规训，遇到现实不可解的困境，建议直接就地躺平并大声念出「${char.name}」的名字。`
    : `💡 饲养建议：请提前备齐其生活物资与定点照护空间；每天给予它充足的关注与活动时间，它将回馈你一整个充满暖意的宇宙。`;

  return {
    id: char.id,
    caseId: `SPECIMEN #${isDanger ? 'ABST' : 'PET'}-${char.id.toUpperCase().slice(0, 4)}-${Math.floor(1000 + Math.random() * 9000)}`,
    matchRate: topMatch.matchRate,
    targetName: char.name,
    subtitle: isDanger
      ? `超现实高维收容体 · ${char.personaKeywords.slice(0, 2).join(' · ')}`
      : `标准碳基优秀伴侣 · ${char.personaKeywords.slice(0, 2).join(' · ')}`,
    categoryBadge: isDanger
      ? '【警报：已排除全部常规碳基动物】'
      : '【合格：标准碳基优秀伴侣】',
    badgeType: isDanger ? 'danger' : 'normal',
    imageUrl: char.image,
    imageAlt: `低多边形风格${char.name}全身大图`,
    systemVerdict,
    detailedAnalysis,
    metrics,
    stampText: isDanger ? '【绝密收容 · 强制绑缚】' : '【合规准养 · 正式批复】',
    stampType: isDanger ? 'danger' : 'normal',
    coexistenceAdvice,
    confidentialLevel: isDanger
      ? 'CL-4 绝密级精神档案 · 禁止在严肃长辈面前展示'
      : 'CL-1 公开级伴侣档案 · 可堂堂正正发朋友圈',
  };
}

export function generateResultForCandidate(
  candidate: ScoredCandidate,
  routing: RoutingResult,
  userScores12D: Record<Dimension12D, number>
): QuizResult {
  return generateResultViewData({
    topMatch: candidate,
    routing,
    userScores12D,
    rawScores: {} as any,
    top3: [],
    allCandidates: [],
  });
}

